/**
 * Loads the CKEditor build the legacy product authors templates in.
 *
 * NOT THE npm PACKAGE. `@ckeditor/ckeditor5-build-classic` is a PREBUILT bundle of 26
 * plugins that cannot be reconfigured, and three of the ones this app needs are simply not
 * in it: `SourceEditing`, `GeneralHtmlSupport` and `FullPage`. Their absence is silent —
 * `htmlSupport` config is accepted and ignored — and the consequence is that a phishing
 * template, which is a COMPLETE HTML DOCUMENT with a doctype, a `<head>` and a `<style>`
 * block, is quietly stripped down to its body on the way through the editor.
 *
 * The build the legacy uses has 46 plugins including those three, and it is ALREADY IN THIS
 * REPOSITORY at `wwwroot/assets/metronic/plugins/custom/ckeditor/` — the same bytes the
 * legacy loads. So this is not a new dependency; it is the same editor, reached the same
 * way the legacy reaches it: one script tag, loaded on demand.
 *
 * ON DEMAND, because it is 1.7 MB. Nothing pays for it until a screen mounts an editor.
 */

const BASE = '/assets/metronic/plugins/custom/ckeditor'

let loading = null

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      if (existing.dataset.loaded === 'true') return resolve()
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error(`Could not load ${src}`)))
      return undefined
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true'
      resolve()
    })
    script.addEventListener('error', () => reject(new Error(`Could not load ${src}`)))
    document.head.appendChild(script)
    return undefined
  })
}

/**
 * @returns {Promise<any>} the global `ClassicEditor` from the local build
 */
export function loadClassicEditor() {
  loading ??= (async () => {
    await loadScript(`${BASE}/ckeditor.js`)
    /* The Arabic UI strings. The legacy sets `language.ui = 'ar'` and never loads this file,
       which is why its editor chrome is English inside an RTL panel. One more script fixes
       a bug we inherited rather than reproducing it. A failure here is not fatal: the editor
       still works, in English. */
    await loadScript(`${BASE}/translations/ar.js`).catch(() => {})
    if (!window.ClassicEditor) throw new Error('CKEditor did not register ClassicEditor')
    return window.ClassicEditor
  })().catch((error) => {
    // Let the next mount try again rather than caching the failure forever.
    loading = null
    throw error
  })

  return loading
}

/**
 * The toolbar, in the legacy's own order.
 *
 * The legacy pasted the CKEditor SUPER-BUILD sample toolbar into a page that loads a
 * 46-plugin custom build, so eleven of its items — exportPDF, exportWord, code, subscript,
 * superscript, removeFormat, todoList, highlight, codeBlock, htmlEmbed, specialCharacters —
 * do not exist there and are dropped at runtime with a console warning each. Asking for
 * them again would reproduce the warnings and change nothing, so this is the legacy's list
 * with those eleven removed and nothing added.
 */
export const TOOLBAR_ITEMS = [
  'findAndReplace',
  'selectAll',
  '|',
  'heading',
  '|',
  'bold',
  'italic',
  'strikethrough',
  'underline',
  '|',
  'bulletedList',
  'numberedList',
  '|',
  'outdent',
  'indent',
  '|',
  'undo',
  'redo',
  '-',
  'fontSize',
  'fontFamily',
  'fontColor',
  'fontBackgroundColor',
  '|',
  'alignment',
  '|',
  'link',
  'uploadImage',
  'blockQuote',
  'insertTable',
  'mediaEmbed',
  '|',
  'horizontalLine',
  'pageBreak',
  '|',
  'textPartLanguage',
  '|',
  /* The raw-HTML toggle. It is a TOOLBAR BUTTON in this build, not a tab someone has to
     build — and it is the whole reason for loading this bundle rather than the npm one. */
  'sourceEditing',
]

/**
 * Images travel INSIDE the HTML as data URIs.
 *
 * A phishing email is sent by a mail server that has no access to this application, and a
 * landing page may be served from a different host entirely — an `<img>` pointing at
 * `/root/…` would be a broken image in both. The legacy reaches the same conclusion and
 * hand-rolls the same adapter.
 */
export function Base64UploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => ({
    upload: async () => {
      const file = await loader.file
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener('load', () => resolve(reader.result))
        reader.addEventListener('error', () => reject(reader.error))
        reader.readAsDataURL(file)
      })
      return { default: dataUrl }
    },
    abort: () => {},
  })
}
